import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CloudRain } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <CloudRain size={32} weight="bold" className="text-blue-600" />
              <span className="text-2xl font-bold tracking-tight text-slate-950" style={{ fontFamily: 'Chivo' }}>WeatherProof</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold tracking-tighter text-slate-950 mb-8" style={{ fontFamily: 'Chivo' }}>Terms of Service</h1>
        
        {/* Summary Box */}
        <div className="mb-12 p-6 bg-blue-50 border border-blue-200">
          <h2 className="text-lg font-semibold text-blue-900 mb-4" style={{ fontFamily: 'Chivo' }}>Terms of Service (Summary)</h2>
          <p className="text-sm text-blue-700 mb-3">By using WeatherProof, you agree to the following:</p>
          <ul className="space-y-2 text-sm text-blue-700">
            <li>• The service provides historical weather data for informational purposes only</li>
            <li>• Reports are not guaranteed to be legally admissible or accepted by third parties</li>
            <li>• You will not misuse the service for fraudulent or unlawful activity</li>
            <li>• Payments for reports are final and non-refundable once generated</li>
            <li>• Service availability is not guaranteed and may change</li>
          </ul>
          <p className="text-sm text-blue-700 mt-3">WeatherProof reserves the right to update or modify the service at any time.</p>
          <p className="text-xs text-blue-600 mt-4 italic">Full terms detailed below.</p>
        </div>
        
        <div className="space-y-8 text-sm text-slate-600 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>1. Acceptance of Terms</h2>
            <p>By accessing and using WeatherProof, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>2. Service Description</h2>
            <p>WeatherProof provides historical weather data retrieval and reporting services for professional use. Our service is intended for informational purposes and should not be used as the sole source for critical legal, insurance, or contractual decisions.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>3. Data Accuracy and Limitations</h2>
            <p className="mb-4">
              <strong>This report is generated using historical weather data from third-party data providers. 
              While data is sourced from reputable services, accuracy is not guaranteed.</strong>
            </p>
            <p className="mb-4">
              <strong>This report is provided for informational and documentation purposes only. 
              Acceptance by courts, insurance companies, municipalities, or other entities is not guaranteed.</strong>
            </p>
            <p className="mb-4">
              <strong>The user is responsible for verifying the suitability of this report for their specific use case.</strong>
            </p>
            <p>Weather data is provided "as is" without warranties of any kind, including but not limited to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Data is sourced from third-party weather providers (Visual Crossing Weather and others)</li>
              <li>Observations may be from nearest available station, not exact requested location</li>
              <li>Timestamps represent nearest available reading to requested time</li>
              <li>Data gaps may exist for certain locations or time periods</li>
              <li>Historical data availability varies by region and date</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>4. User Accounts</h2>
            <p>You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately of any unauthorized use of your account.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>5. Payments and Refunds</h2>
            <p className="mb-3">
              <strong>Report Purchases:</strong> Each weather report costs $9.99 USD. All payments are processed securely through Stripe.
            </p>
            <p className="mb-3">
              <strong>Non-Refundable:</strong> Payments for reports are final and non-refundable once the report has been generated. 
              By completing payment, you acknowledge that you have reviewed the free weather lookup data and accept the report 
              based on that information.
            </p>
            <p className="mb-3">
              <strong>Why Non-Refundable:</strong> Reports are custom-generated documents with unique IDs. Once generated, 
              the report data cannot be "returned" and has already been provided to you via email and download.
            </p>
            <p className="mb-3">
              <strong>Payment Issues:</strong> If you experience technical issues during payment or report generation, 
              contact support@weatherproof.com within 48 hours with your session ID.
            </p>
            <p>
              <strong>Disputes:</strong> All payment disputes must be addressed directly through our support team. 
              Chargeback fraud will result in immediate account termination and may be reported to authorities.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>6. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Misrepresent data or reports generated by our service</li>
              <li>Attempt to reverse engineer or scrape our service</li>
              <li>Share account credentials with others</li>
              <li>Use the service for illegal purposes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>7. Limitation of Liability</h2>
            <p>WeatherProof shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use the service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>8. Service Availability</h2>
            <p className="mb-3">
              <strong>No Uptime Guarantee:</strong> While we strive to maintain reliable service, WeatherProof does not guarantee 
              uninterrupted access. The service may be unavailable due to maintenance, technical issues, or third-party API failures.
            </p>
            <p className="mb-3">
              <strong>Third-Party Dependencies:</strong> Our service relies on external weather data providers and payment processors. 
              We are not responsible for outages or errors caused by these third parties.
            </p>
            <p>
              <strong>Service Changes:</strong> We reserve the right to modify, suspend, or discontinue any part of the service 
              at any time without prior notice. Features may be added or removed as we improve the platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>9. Changes to Terms</h2>
            <p>We reserve the right to modify these terms at any time. Continued use of the service constitutes acceptance of modified terms.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-950 mb-4" style={{ fontFamily: 'Chivo' }}>10. Contact</h2>
            <p>For questions about these Terms of Service, contact us at: legal@weatherproof.com</p>
          </section>

          <div className="mt-12 pt-6 border-t border-slate-200">
            <p className="text-xs text-slate-500">Last updated: {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
